import { Empty, InputRef, RadioChangeEvent } from 'antd';
import { CheckboxOptionType } from 'antd/lib/checkbox/Group';
import { useEffect, useRef, useState } from 'react';

import { RadioOptions } from 'components/GlobalFilter/constants';
import { Button, Card, CheckboxGroup, Radio, RadioGroup, Skeleton, Input } from 'components/common';

import { usePerformance } from '../../modules/performance/usePerformance';

interface GlobalFilterTemplateProps {
  label: string;
  checkboxOptions: CheckboxOptionType[];
  selectedOptions: CheckboxOptionType[];
  radioOption: RadioOptions;
  handleDimensionRadioOption?: React.Dispatch<React.SetStateAction<RadioOptions>>;
  handleSubmitAction: (data: {
    radioOption: RadioOptions;
    checkboxOptions: CheckboxOptionType[];
  }) => void;
  isSearchBased?: boolean;
  onSearch?: (query: string) => void;
  searchLoading?: boolean;
}

export const GlobalFilterTemplate = ({
  label,
  checkboxOptions,
  handleSubmitAction,
  radioOption,
  handleDimensionRadioOption,
  selectedOptions,
  isSearchBased = false,
  onSearch,
  searchLoading = false,
}: GlobalFilterTemplateProps) => {
  const [radioValue, setRadioValue] = useState<RadioOptions>(radioOption);
  const [selectedCheckboxes, setSelectedCheckboxes] = useState<CheckboxOptionType[]>(
    selectedOptions || [],
  );
  const { activeTab } = usePerformance();
  const [searchQuery, setSearchQuery] = useState('');

  const inputRef = useRef<InputRef>(null);

  const handleClick = () => {
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  useEffect(() => {
    if (isSearchBased && onSearch && searchQuery.length > 2) {
      const timeoutId = setTimeout(() => {
        onSearch(searchQuery);
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [searchQuery, isSearchBased, onSearch]);

  const isCampaignSingleSelect =
    label === 'Campaign' && (activeTab === 'INCREMENTALITY' || activeTab === 'RIPPLE_EFFECT');

  const filteredCheckboxOptions = isSearchBased
    ? checkboxOptions
    : checkboxOptions?.filter((option) =>
        option.value?.toString().toLowerCase().includes(searchQuery.toLowerCase()),
      );

  // For search-based dimensions, always include previously selected options
  const allVisibleOptions = isSearchBased
    ? [
        // Always include previously selected options first (even if they don't match current search)
        ...selectedCheckboxes.map(
          (selected) =>
            ({
              label: String(selected),
              value: selected,
              disabled: false,
            }) as CheckboxOptionType,
        ),
        // Add current search results that aren't already selected
        ...(filteredCheckboxOptions || []).filter(
          (option) => !selectedCheckboxes.some((selected) => selected === option.value),
        ),
      ]
    : filteredCheckboxOptions;

  const processedOptions: CheckboxOptionType[] =
    allVisibleOptions?.map((option) => {
      const isSelected = selectedCheckboxes?.some((selected) => selected === option.value);

      let disabled = false;

      if (isSelected) {
        disabled = false;
      } else if (
        (isCampaignSingleSelect &&
          selectedCheckboxes?.length === 1 &&
          !selectedCheckboxes?.includes(option.value)) ||
        option.disabled
      ) {
        disabled = true;
      }

      return {
        ...option,
        disabled,
      } as CheckboxOptionType;
    }) || [];

  useEffect(() => {
    // For search-based dimensions, only update if:
    // 1. selectedOptions has actual values (not empty)
    // 2. OR we don't have any local selections yet
    // This prevents parent from resetting our local selections during search
    if (!isSearchBased || selectedOptions.length > 0 || selectedCheckboxes.length === 0) {
      setSelectedCheckboxes(selectedOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOptions]);

  const handleRadioChange = (e: RadioChangeEvent) => {
    setRadioValue(e.target.value);
    handleDimensionRadioOption && handleDimensionRadioOption(e.target.value);
  };

  const handleCheckboxChange = (newlyChecked: CheckboxOptionType[]) => {
    if (isSearchBased) {
      // For search-based dimensions, simply use the newlyChecked values directly
      // because our allVisibleOptions already includes all previously selected items
      setSelectedCheckboxes(newlyChecked);
    } else {
      // Original logic for non-search based dimensions
      const currentVisibleValues = processedOptions.map((opt) => opt.value);
      const filteredPrevious = selectedCheckboxes.filter(
        (val) => !currentVisibleValues.includes(val),
      );
      const updated = [...filteredPrevious, ...newlyChecked];

      setSelectedCheckboxes(updated);
    }
  };

  return (
    <Card style={{ width: 384 }} className="filter-container p-6">
      <div className="pb-1.5 font-medium">{label}</div>
      <div className="pb-1.5 justify-start items-start gap-6 inline-flex">
        <RadioGroup onChange={handleRadioChange} value={radioValue}>
          <Radio value={RadioOptions.INCLUDE}>is</Radio>
          <Radio value={RadioOptions.EXCLUDE}>is not</Radio>
        </RadioGroup>
      </div>
      <Card className="filter-card max-h-[212px] p-1.5 bg-primary-gray-50 border border-primary-gray-200 overflow-auto">
        {isSearchBased ? (
          <>
            <Input
              ref={inputRef}
              placeholder={
                searchQuery.length <= 2
                  ? 'Enter at least 3 characters to search...'
                  : 'Search values...'
              }
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="mb-2"
              onClick={handleClick}
            />
            {searchLoading ? (
              <Skeleton loading={true} />
            ) : searchQuery.length > 2 ? (
              processedOptions && processedOptions.length > 0 ? (
                <CheckboxGroup
                  options={processedOptions}
                  onChange={handleCheckboxChange}
                  value={selectedCheckboxes}
                />
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No results found" />
              )
            ) : selectedCheckboxes.length > 0 ? (
              <CheckboxGroup
                options={selectedCheckboxes.map(
                  (selected) =>
                    ({
                      label: String(selected),
                      value: selected,
                      disabled: false,
                    }) as CheckboxOptionType,
                )}
                onChange={handleCheckboxChange}
                value={selectedCheckboxes}
              />
            ) : (
              <div className="text-gray-500 text-sm p-2 text-center">
                Please enter at least 3 characters to search
              </div>
            )}
          </>
        ) : checkboxOptions ? (
          checkboxOptions.length > 0 ? (
            <>
              <Input
                ref={inputRef}
                placeholder="Search values..."
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="mb-2"
                onClick={handleClick}
              />
              <CheckboxGroup
                options={processedOptions}
                onChange={handleCheckboxChange}
                value={selectedCheckboxes}
              />
            </>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data to show" />
          )
        ) : (
          <Skeleton loading={true} />
        )}
      </Card>
      <Button
        size="md"
        variant="primary"
        className="w-[336px] mt-1.5"
        disabled={!selectedCheckboxes?.length}
        onClick={() =>
          handleSubmitAction({
            radioOption: radioValue,
            checkboxOptions: selectedCheckboxes,
          })
        }
      >
        Apply
      </Button>
    </Card>
  );
};
